export const isString = function (arg: unknown) {
  return typeof arg === 'string';
};
export const isDate = function (arg: unknown): arg is Date {
  return arg instanceof Date;
};

export const isObject = function (arg: unknown): arg is object {
  return arg !== null && typeof arg === 'object';
};

/**
 * Checks if a value is an object-like (i.e., not an array, date, map, etc.).
 * @param v The value to check.
 * @returns True if the value is object-like, false otherwise.
 */
export function isObjectLike(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export const isFunction = (
  value: unknown
): value is (...args: unknown[]) => unknown =>
  !!value &&
  (Object.prototype.toString.call(value) === '[object Function]' ||
    typeof value === 'function');

export const isUUID = (s: string) =>
  !!s.match(
    '^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[0-9a-dA-D][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000)$'
  );

export function isNullOrEmpty(value: unknown): boolean {
  if (value == null || value === '') return true;
  return false;
}

export function isPresent(value: unknown) {
  return value !== null && value !== undefined;
}

const toCamel = (str: string): string => {
  // insert word boundaries, then build camelCase
  const parts = str
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);

  if (!parts.length) return str;

  return parts
    .map((w, i) =>
      i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()
    )
    .join('');
};

/**
 * Checks if a value is a plain object (i.e., not an array, date, map, etc.).
 * Strict check.
 * @param v The value to check.
 * @returns True if the value is a plain object, false otherwise.
 */
export function isPlainObject(v: unknown): v is Record<string, unknown> {
  // filters arrays/dates/maps/boxed primitives/etc.
  if (Object.prototype.toString.call(v) !== '[object Object]') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === null || proto === Object.prototype;
}

/**
 * Converts the keys of an object or array to camelCase.
 * @param input The object or array to convert.
 * @returns The converted object or array.
 */
export function keysToCamel(input: unknown): unknown {
  if (Array.isArray(input)) return input.map(keysToCamel);
  if (isPlainObject(input)) {
    return Object.fromEntries(
      Object.entries(input).map(([k, v]) => [toCamel(k), keysToCamel(v)])
    );
  }
  return input;
}
